import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import DataTable from '../../components/common/DataTable/DataTable';
import SummaryCards from '../../components/common/SummaryCards/SummaryCards';
import Button from '../../components/common/Button/Button';
import DateRangeModal from '../../components/common/DateRangeModal/DateRangeModal';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import { usePayments, type PaymentFilters } from '../../hooks/usePayments';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { PAYMENT_SYSTEM_LABELS } from '../../types/payment';
import { formatDate, formatCurrency } from '../../utils';
import styles from './Payments.module.css';

const PAYMENT_SYSTEM_VALUES = ['asaas', 'stripe', 'pagarme', 'mercadopago', 'paypal', 'pix'];
const PAYMENT_DATE_FILTER_VALUES = ['7days', '30days', '90days', 'custom'];
const PAYMENTS_QUERY_SCHEMA = {
  searchName: queryFilter.string(),
  filterSystems: queryFilter.array<string>([], { allowed: PAYMENT_SYSTEM_VALUES }),
  dateFilters: queryFilter.array<string>([], { allowed: PAYMENT_DATE_FILTER_VALUES }),
  customDateStart: queryFilter.string(),
  customDateEnd: queryFilter.string(),
};

const Payments: React.FC = () => {
  const navigate = useNavigate();
  const {
    filters: {
      searchName,
      filterSystems,
      dateFilters,
      customDateStart,
      customDateEnd,
    },
    setFilter,
    setFilters,
    resetFilters,
  } = useQueryFilters(PAYMENTS_QUERY_SCHEMA);
  const [showDateModal, setShowDateModal] = useState(false);
  const paymentFilters: PaymentFilters = useMemo(() => ({
    searchName: searchName || undefined,
    systems: filterSystems.length > 0 ? filterSystems : undefined,
    dates: dateFilters.length > 0 ? dateFilters : undefined,
    customDateStart: customDateStart || undefined,
    customDateEnd: customDateEnd || undefined,
  }), [customDateEnd, customDateStart, dateFilters, filterSystems, searchName]);
  const {
    rows: allRows,
    filteredRows: filtered,
    loading,
    error,
  } = usePayments(paymentFilters);

  const hasActiveFilters = !!(
    searchName
    || filterSystems.length > 0
    || dateFilters.length > 0
    || customDateStart
    || customDateEnd
  );

  const handleSystemsFilterChange = useCallback((newValues: string[]) => {
    setFilter('filterSystems', newValues);
  }, [setFilter]);

  const handleDateFilterChange = useCallback((values: string[]) => {
    const justAddedCustom = values.includes('custom') && !dateFilters.includes('custom');
    setFilter('dateFilters', values);
    if (justAddedCustom) {
      setShowDateModal(true);
    }
  }, [dateFilters, setFilter]);

  const systemsOptions = useMemo(() => [
    { value: 'asaas', label: 'Asaas' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'pagarme', label: 'Pagar.me' },
    { value: 'mercadopago', label: 'Mercado Pago' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'pix', label: PAYMENT_SYSTEM_LABELS.pix },
  ], []);

  const dateOptions = useMemo(() => [
    { value: '7days', label: 'Últimos 7 dias' },
    { value: '30days', label: 'Últimos 30 dias' },
    { value: '90days', label: 'Últimos 90 dias' },
    { value: 'custom', label: customDateStart || customDateEnd
      ? `de ${customDateStart ? formatDate(customDateStart) : '...'} até ${customDateEnd ? formatDate(customDateEnd) : '...'}`
      : 'Personalizado' },
  ], [customDateStart, customDateEnd]);

  const allOptionsMap = useMemo(() => {
    const map: Record<string, Record<string, { label: string }>> = {};
    map.systems = {};
    for (const o of systemsOptions) map.systems[o.value] = o;
    map.date = {};
    for (const o of dateOptions) map.date[o.value] = o;
    return map;
  }, [systemsOptions, dateOptions]);

  const handleRemoveChip = useCallback((filterKey: string, value: string) => {
    if (filterKey === 'systems') {
      setFilter('filterSystems', (current) => current.filter((item) => item !== value));
    } else if (filterKey === 'date') {
      setFilter('dateFilters', (current) => current.filter((item) => item !== value));
    }
  }, [setFilter]);

  const unifiedChips: FilterChipEntry[] = useMemo(() => {
    return [
      ...filterSystems.map((value) => ({ filterKey: 'systems', value })),
      ...dateFilters.map((value) => ({ filterKey: 'date', value })),
    ].map((selection) => {
      const opt = allOptionsMap[selection.filterKey]?.[selection.value];
      return {
        filterKey: selection.filterKey,
        value: selection.value,
        label: opt?.label ?? selection.value,
        onRemove: (value: string) => handleRemoveChip(selection.filterKey, value),
      };
    });
  }, [filterSystems, dateFilters, allOptionsMap, handleRemoveChip]);

  const handleDateModalConfirm = (start: string, end: string) => {
    setFilters({
      customDateStart: start,
      customDateEnd: end,
    });
    setShowDateModal(false);
  };

  const handleDateModalCancel = () => {
    setFilter('dateFilters', (current) => current.filter((filter) => filter !== 'custom'));
    setShowDateModal(false);
  };

  /** Summary metrics */
  const totalPaid = allRows.reduce((sum, r) => sum + r.value, 0);
  const totalPayments = allRows.length;
  const uniqueStudents = new Set(allRows.map((r) => r.studentId)).size;

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Planos e pagamentos"
          subtitle="Carregando pagamentos..."
        />
        <SearchFilterBar
          searchValue=""
          onSearchChange={() => {}}
          searchPlaceholder="Pesquisar"
          loading
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Planos e pagamentos"
        subtitle={`${totalPayments} pagamento(s) registrado(s)`}
      />

      {/* ── Summary cards ── */}
      <SummaryCards
        items={[
          { label: 'Total recebido', value: formatCurrency(totalPaid) },
          { label: 'Planos e pagamentos', value: totalPayments },
          { label: 'Alunas pagantes', value: uniqueStudents },
        ]}
      />

      <FilterBar gap="lg">
        <FilterGroup>
          <SearchFilterBar
            searchValue={searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={filterSystems}
            onChange={handleSystemsFilterChange}
            options={systemsOptions}
            placeholder="Todos os sistemas"
            clearButtonLabel="Limpar filtro"
            hideChips
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={dateFilters}
            onChange={handleDateFilterChange}
            options={dateOptions}
            placeholder="Data do pagamento"
            clearButtonLabel="Limpar filtro"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={resetFilters} />
        )}
      </FilterBar>

      <FilterChipsBar chips={unifiedChips} />

      {/* ── Table ── */}
      {filtered.length === 0 ? (
        <EmptyState message="Nenhum pagamento encontrado com os filtros selecionados." />
      ) : (
        <DataTable
          headers={['Aluna', 'Sistema', 'Data', 'Valor', 'ID Externo', 'Ações']}
          gridTemplate="1fr 100px 110px 100px 220px 80px"
          highlightTerm={searchName}
        >
              {filtered.map((row) => (
                  <DataTable.Row key={row.paymentId}>
                    <div className={styles['student-name']}>{row.studentName}</div>
                    <div>
                      <span className={styles['system-badge']}>
                        {PAYMENT_SYSTEM_LABELS[row.paymentSystem]}
                      </span>
                    </div>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(row.confirmedDate)}
                    </div>
                    <div className={styles['value-cell']}>
                      {formatCurrency(row.value)}
                    </div>
                    <div className={styles['external-id-cell']}>
                      {row.paymentExternalId}
                    </div>
                    <div>
                      <Button
                        variant="primary"
                        size="xs"
                        icon="visibility"
                        onClick={() => navigate(`/alunas/${row.studentId}/editar`)}
                      >
                        Aluna
                      </Button>
                    </div>
                  </DataTable.Row>
              ))}
        </DataTable>
      )}

      <DateRangeModal
        open={showDateModal}
        onConfirm={handleDateModalConfirm}
        onCancel={handleDateModalCancel}
        initialStart={customDateStart}
        initialEnd={customDateEnd}
      />
    </div>
  );
};

export default Payments;
